/**
 * Logistics. Every SUPPLY_INTERVAL ticks each faction's territory is analysed:
 *  1. connected components of owned hexes → the component holding the capital (else the richest)
 *     is the MAIN network; units standing in any other component are encircled (KESSEL events);
 *  2. multi-source Dijkstra from owned cities through own territory: each city pushes a budget
 *     (SUPPLY_RANGE by size, halved inside pockets), hexes spend terrain.supplyCost (roads/rails
 *     cheaper, unbridged rivers dearer, hostile-occupied hexes block). Hex supply =
 *     min(1, remaining budget / SUPPLY_FALLOFF);
 *  3. abandoned enclaves (no city, no friendly unit) are mopped up by their neighbours.
 * Every tick units read their supply from the latest networks and suffer attrition when starved.
 */
import {
  LOW_SUPPLY,
  MIN_STRENGTH,
  MOPUP_CHANCE,
  MUD_SUPPLY_COST,
  OOS_ORG_DECAY,
  OOS_STRENGTH_DECAY,
  POCKET_SOURCE_FACTOR,
  RAIL_SUPPLY_FACTOR,
  RAILWAYS_SUPPLY_BONUS,
  RIVER_SUPPLY_FACTOR,
  ROAD_SUPPLY_FACTOR,
  SNOW_ATTRITION,
  SUPPLY_FALLOFF,
  SUPPLY_NEED,
  SUPPLY_RANGE,
  SURRENDER_AFTER_HOURS,
  SURRENDER_ORG,
  TERRAIN_STATS,
  UNIT_STATS,
  ZERO_SUPPLY,
} from '../core/constants';
import type { HexId } from '../core/hex';
import { type FactionId, NO_FACTION, type Unit } from '../core/types';
import { effectiveTerrain, riverFrozen, W_MUD, W_SNOW, W_SNOWING } from '../core/weather';
import {
  audienceOf,
  detectorsOf,
  emit,
  hasTech,
  hostileAt,
  isHostileOwner,
  type SimContext,
  type SupplyNetwork,
} from './context';
import { captureHex } from './control';
import { MinHeap } from './heap';
import { hasRail, hasRiver, hasRoad } from './movecost';
import { destroyUnit } from './units';

const heap = new MinHeap();
/**
 * Remaining budget per hex. Must be float64: the heap keys are float64, and a float32 store rounds
 * some budgets up so `r < rem[h]` wrongly skips expanding those hexes (starving whole areas).
 */
let remaining: Float64Array | null = null;

export function emptyNetwork(count: number): SupplyNetwork {
  return { comp: new Int32Array(count).fill(-1), main: -1, compHasSource: [] };
}

/** Label connected components of every faction's territory in one pass over the map. */
function labelComponents(ctx: SimContext): void {
  const { state, grid } = ctx;
  const owner = state.owner;
  const nb = grid.neighbours;
  for (const net of ctx.networks) {
    net.comp.fill(-1);
    net.compHasSource = [];
  }
  const stack: HexId[] = [];
  for (let h = 0; h < grid.count; h++) {
    const f = owner[h];
    if (f === NO_FACTION) continue;
    const net = ctx.networks[f];
    if (net.comp[h] >= 0) continue;
    const id = net.compHasSource.length;
    net.compHasSource.push(0);
    net.comp[h] = id;
    stack.push(h);
    while (stack.length) {
      const cur = stack.pop()!;
      for (let d = 0; d < 6; d++) {
        const n = nb[cur * 6 + d];
        if (n >= 0 && owner[n] === f && net.comp[n] < 0) {
          net.comp[n] = id;
          stack.push(n);
        }
      }
    }
  }
  // Sources and the main network.
  const compVp: number[][] = ctx.networks.map((n) => new Array<number>(n.compHasSource.length).fill(0));
  for (const city of ctx.map.cities) {
    const f = owner[city.hex];
    if (f === NO_FACTION) continue;
    const c = ctx.networks[f].comp[city.hex];
    ctx.networks[f].compHasSource[c] = 1;
    compVp[f][c] += city.vp + 1;
  }
  for (let f = 0; f < ctx.networks.length; f++) {
    const net = ctx.networks[f];
    const capital = ctx.map.cities[state.factions[f].capitalCity];
    if (capital && owner[capital.hex] === f) {
      net.main = net.comp[capital.hex];
      continue;
    }
    net.main = -1;
    let best = 0;
    for (let c = 0; c < compVp[f].length; c++) {
      if (compVp[f][c] > best) {
        best = compVp[f][c];
        net.main = c;
      }
    }
  }
}

/**
 * Supply cost of pushing supply from `from` into its neighbour `to` for faction f. Weather: frozen
 * marsh carries like plains and frozen rivers need no bridge; off-road columns bog down in mud.
 */
function edgeCost(ctx: SimContext, from: HexId, to: HexId, dir: number, railways: boolean): number {
  const { map } = ctx;
  const weather = ctx.state.weather.conditions;
  const base = TERRAIN_STATS[effectiveTerrain(map.terrain[to], weather[to])].supplyCost;
  const rail = hasRail(map, from, dir);
  const road = hasRoad(map, from, dir);
  if (rail || road)
    return (base * (rail ? RAIL_SUPPLY_FACTOR : ROAD_SUPPLY_FACTOR)) / (railways ? RAILWAYS_SUPPLY_BONUS : 1);
  const river = hasRiver(map, from, dir) && !riverFrozen(weather, from, to) ? RIVER_SUPPLY_FACTOR : 1;
  return base * river * (weather[to] & W_MUD ? MUD_SUPPLY_COST : 1);
}

function propagate(ctx: SimContext, f: FactionId): void {
  const { state, grid, map } = ctx;
  const owner = state.owner;
  const net = ctx.networks[f];
  const level = state.supply[f];
  level.fill(0);
  if (!remaining || remaining.length !== grid.count) remaining = new Float64Array(grid.count);
  const rem = remaining;
  rem.fill(-1);
  heap.clear();
  const railways = hasTech(state, f, 'railways');
  for (const city of map.cities) {
    if (owner[city.hex] !== f || hostileAt(ctx, city.hex, f)) continue;
    let budget =
      city.capitalOf === f ? SUPPLY_RANGE.capital : city.size >= 2 ? SUPPLY_RANGE.city : SUPPLY_RANGE.town;
    if (net.comp[city.hex] !== net.main) budget *= POCKET_SOURCE_FACTOR;
    if (budget > rem[city.hex]) {
      rem[city.hex] = budget;
      heap.push(city.hex, -budget);
    }
  }
  const nb = grid.neighbours;
  while (heap.size > 0) {
    const r = -heap.peekKey();
    const h = heap.pop();
    if (r < rem[h]) continue;
    for (let d = 0; d < 6; d++) {
      const n = nb[h * 6 + d];
      if (n < 0 || owner[n] !== f || hostileAt(ctx, n, f)) continue;
      const nr = r - edgeCost(ctx, h, n, d, railways);
      if (nr > 0 && nr > rem[n]) {
        rem[n] = nr;
        heap.push(n, -nr);
      }
    }
  }
  for (let h = 0; h < grid.count; h++) if (rem[h] > 0) level[h] = Math.min(1, rem[h] / SUPPLY_FALLOFF);
}

/** Recompute all supply networks and flag encircled units; `announce` emits KESSEL events. */
export function computeSupply(ctx: SimContext, announce: boolean): void {
  labelComponents(ctx);
  for (let f = 0; f < ctx.state.factions.length; f++) propagate(ctx, f);
  updateEncirclement(ctx, announce);
}

function isEncircled(ctx: SimContext, u: Unit): boolean {
  const g = ctx.state.owner[u.hex];
  if (g === NO_FACTION || ctx.teamOf[g] !== ctx.teamOf[u.faction]) return false;
  const net = ctx.networks[g];
  return net.main < 0 || net.comp[u.hex] !== net.main;
}

function updateEncirclement(ctx: SimContext, announce: boolean): void {
  const pockets = new Map<number, Unit[]>();
  for (const u of ctx.state.units.values()) {
    const enc = isEncircled(ctx, u);
    if (enc && !u.encircled && announce) {
      const g = ctx.state.owner[u.hex];
      const key = (u.faction * 8 + g) * ctx.grid.count + ctx.networks[g].comp[u.hex];
      const list = pockets.get(key);
      if (list) list.push(u);
      else pockets.set(key, [u]);
    }
    u.encircled = enc;
  }
  for (const units of pockets.values()) {
    const faction = units[0].faction;
    emit(
      ctx,
      { kind: 'encircled', faction, units: units.map((u) => u.id), hex: units[0].hex },
      audienceOf(ctx, [faction]),
    );
    // Enemies watching the pocket hear about the units they can actually see.
    const seen = new Map<FactionId, Unit[]>();
    for (const u of units)
      for (const g of detectorsOf(ctx, u.id, faction)) seen.set(g, [...(seen.get(g) ?? []), u]);
    for (const [g, list] of [...seen].sort((a, b) => a[0] - b[0])) {
      emit(ctx, { kind: 'encircled', faction, units: list.map((u) => u.id), hex: list[0].hex }, [g]);
    }
  }
}

/** Abandoned enclaves — no city, no friendly unit — fall to the hostile neighbour hex by hex. */
export function mopUpEnclaves(ctx: SimContext): void {
  const { state, grid, teamOf } = ctx;
  const owner = state.owner;
  const hasUnits = ctx.networks.map((n) => new Uint8Array(n.compHasSource.length));
  for (const u of state.units.values()) {
    const g = owner[u.hex];
    if (g === NO_FACTION || teamOf[g] !== teamOf[u.faction]) continue;
    const c = ctx.networks[g].comp[u.hex];
    if (c >= 0) hasUnits[g][c] = 1;
  }
  const counts = new Array<number>(state.factions.length);
  const flips: number[] = [];
  const nb = grid.neighbours;
  for (let h = 0; h < grid.count; h++) {
    const f = owner[h];
    if (f === NO_FACTION) continue;
    const net = ctx.networks[f];
    const c = net.comp[h];
    if (c < 0 || net.compHasSource[c] || hasUnits[f][c]) continue;
    counts.fill(0);
    let taker = -1;
    for (let d = 0; d < 6; d++) {
      const n = nb[h * 6 + d];
      if (n < 0) continue;
      const g = owner[n];
      if (!isHostileOwner(ctx, g, f)) continue;
      counts[g]++;
      if (taker < 0 || counts[g] > counts[taker]) taker = g;
    }
    if (taker >= 0 && ctx.rng.chance(MOPUP_CHANCE)) flips.push(h, taker);
  }
  for (let i = 0; i < flips.length; i += 2) captureHex(ctx, flips[i], flips[i + 1]);
}

/** Unit supply = best supply of its team at its hex, relative to the unit type's appetite. */
export function refreshUnitSupply(ctx: SimContext): void {
  const { state, teamOf } = ctx;
  for (const u of state.units.values()) {
    let lvl = 0;
    for (let g = 0; g < teamOf.length; g++)
      if (teamOf[g] === teamOf[u.faction]) lvl = Math.max(lvl, state.supply[g][u.hex]);
    u.supply = Math.min(1, lvl / (SUPPLY_NEED * UNIT_STATS[u.type].supplyUse));
  }
}

/**
 * Hourly: refresh unit supply; starved units lose org and strength (×SNOW_ATTRITION in snow), and
 * surrender after SURRENDER_AFTER_HOURS at zero supply once their org is broken.
 */
export function updateUnitSupply(ctx: SimContext): void {
  refreshUnitSupply(ctx);
  const weather = ctx.state.weather.conditions;
  for (const u of [...ctx.state.units.values()]) {
    if (u.supply < LOW_SUPPLY) {
      // Starving in the snow is worse.
      const cold = weather[u.hex] & (W_SNOW | W_SNOWING) ? SNOW_ATTRITION : 1;
      const k = (1 - u.supply / LOW_SUPPLY) * cold;
      u.org = Math.max(0, u.org - OOS_ORG_DECAY * k);
      u.strength -= OOS_STRENGTH_DECAY * k;
    }
    u.hoursOutOfSupply = u.supply < ZERO_SUPPLY ? u.hoursOutOfSupply + 1 : 0;
    if (u.hoursOutOfSupply >= SURRENDER_AFTER_HOURS && u.org < SURRENDER_ORG) {
      destroyUnit(ctx, u, 'surrender', nearestHostileFaction(ctx, u));
    } else if (u.strength <= MIN_STRENGTH) {
      destroyUnit(ctx, u, 'attrition', null);
    }
  }
}

/** Whoever has troops next to a starving unit takes its surrender. */
function nearestHostileFaction(ctx: SimContext, u: Unit): FactionId | null {
  for (let r = 1; r <= 3; r++) {
    for (const h of ctx.grid.ring(u.hex, r)) {
      for (const v of ctx.occ.unitsAt(h))
        if (ctx.teamOf[v.faction] !== ctx.teamOf[u.faction]) return v.faction;
    }
  }
  return null;
}
